import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import Navbar from "../../components/Navbar/Navbar";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import Footer from "../../components/footer/Footer";
import Recomended from "../../components/Recomended/Recomended";

const ORDER_STATUSES = ["Pending", "Processing", "Dispatched", "Delivered", "Cancelled"];
const PAYMENT_STATUSES = ["Pending", "Completed", "Failed"];
const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "amount_high", label: "Amount: high to low" },
  { value: "amount_low", label: "Amount: low to high" },
];

const STATUS_TONE = {
  Pending: "gold",
  Processing: "gold",
  Dispatched: "blue",
  Delivered: "green",
  Cancelled: "red",
};

const PAYMENT_TONE = {
  Pending: "gold",
  Completed: "green",
  Failed: "red",
};

const formatDate = (iso, withTime = true) => {
  if (!iso) return "—";
  try {
    const opts = withTime
      ? { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }
      : { day: "2-digit", month: "short", year: "numeric" };
    return new Date(iso).toLocaleString(undefined, opts);
  } catch {
    return iso;
  }
};

const fmtMoney = (n) => Number(n || 0).toFixed(2);

const YourOrdersPage = (props) => {
  const { token } = props;
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState({
    page: parseInt(searchParams.get("page")) || 1,
    limit: parseInt(searchParams.get("limit")) || 10,
    orderStatus: searchParams.get("orderStatus") || "",
    paymentStatus: searchParams.get("paymentStatus") || "",
    from: searchParams.get("from") || "",
    to: searchParams.get("to") || "",
    search: searchParams.get("search") || "",
    sort: searchParams.get("sort") || "newest",
  });

  const [searchInput, setSearchInput] = useState(filters.search);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeOrder, setActiveOrder] = useState(null);

  const debounceRef = useRef(null);

  const syncUrlParams = (next) => {
    const sp = new URLSearchParams();
    Object.entries(next).forEach(([k, v]) => {
      if (v && v !== "newest" && k !== "limit") sp.set(k, v);
    });
    setSearchParams(sp, { replace: true });
  };

  const updateFilter = (name, value, resetPage = true) => {
    setFilters((prev) => {
      const next = { ...prev, [name]: value };
      if (resetPage) next.page = 1;
      syncUrlParams(next);
      return next;
    });
  };

  // debounce search input
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (searchInput !== filters.search) {
        updateFilter("search", searchInput);
      }
    }, 380);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const fetchOrders = async () => {
    setLoading(true);
    if (error) setError(false);

    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== "" && v != null) params[k] = v;
    });

    try {
      const res = await axios.get("order-history", {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(res?.data?.data || []);
      setTotalRecords(res?.data?.totalRecords || 0);
      setTotalPages(res?.data?.totalPages || 1);
      setStats(res?.data?.stats || null);
    } catch (err) {
      // 200/data:[] handled by ok branch; 4xx falls here
      setOrders([]);
      setTotalRecords(0);
      setTotalPages(1);
      if (err?.response?.status !== 400) {
        toast.error(err?.response?.data?.message || "Couldn't load orders");
      }
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.page,
    filters.limit,
    filters.orderStatus,
    filters.paymentStatus,
    filters.from,
    filters.to,
    filters.search,
    filters.sort,
  ]);

  const activeChips = useMemo(() => {
    const chips = [];
    if (filters.orderStatus) chips.push({ key: "orderStatus", label: `Status: ${filters.orderStatus}` });
    if (filters.paymentStatus) chips.push({ key: "paymentStatus", label: `Payment: ${filters.paymentStatus}` });
    if (filters.from) chips.push({ key: "from", label: `From: ${formatDate(filters.from, false)}` });
    if (filters.to) chips.push({ key: "to", label: `To: ${formatDate(filters.to, false)}` });
    if (filters.search) chips.push({ key: "search", label: `“${filters.search}”` });
    return chips;
  }, [filters]);

  const clearAll = () => {
    setSearchInput("");
    setFilters((prev) => {
      const next = {
        ...prev,
        page: 1,
        orderStatus: "",
        paymentStatus: "",
        from: "",
        to: "",
        search: "",
        sort: "newest",
      };
      syncUrlParams(next);
      return next;
    });
  };

  const removeChip = (key) => {
    if (key === "search") setSearchInput("");
    updateFilter(key, "");
  };

  const openDrawer = (order) => {
    setActiveOrder(order);
    setDrawerOpen(true);
  };
  const closeDrawer = () => {
    setDrawerOpen(false);
    setTimeout(() => setActiveOrder(null), 250);
  };

  const isFiltering = activeChips.length > 0 || filters.sort !== "newest";

  return (
    <Fragment>
      <Navbar />

      <section className="gpoh-section">
        <div className="gpoh-bg-orb gpoh-bg-orb-tl" />
        <div className="gpoh-bg-orb gpoh-bg-orb-br" />

        <div className="gpoh-inner">
          {/* HERO */}
          <div className="gpoh-head">
            <div className="gpoh-eyebrow">
              <span className="gpoh-eyebrow-dot" />
              MY ORDERS
            </div>
            <h1 className="gpoh-title">
              Your <span className="gpoh-title-accent">order history</span>
            </h1>
            <p className="gpoh-sub">
              Track, search and review every gift card you've ordered with us.
            </p>
          </div>

          {/* STATS */}
          {stats && (
            <div className="gpoh-stats">
              <div className="gpoh-stat">
                <span className="gpoh-stat-label">Total Orders</span>
                <span className="gpoh-stat-val">{stats.totalOrders || 0}</span>
              </div>
              <div className="gpoh-stat">
                <span className="gpoh-stat-label">Total Spend</span>
                <span className="gpoh-stat-val gpoh-stat-gold">AED {fmtMoney(stats.totalSpend)}</span>
              </div>
              <div className="gpoh-stat">
                <span className="gpoh-stat-label">In Progress</span>
                <span className="gpoh-stat-val">
                  {(stats.pendingCount || 0) + (stats.processingCount || 0) + (stats.dispatchedCount || 0)}
                </span>
              </div>
              <div className="gpoh-stat">
                <span className="gpoh-stat-label">Delivered</span>
                <span className="gpoh-stat-val gpoh-stat-green">{stats.deliveredCount || 0}</span>
              </div>
            </div>
          )}

          {/* FILTER BAR */}
          <div className="gpoh-filter-bar">
            <div className="gpoh-search">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Search by order ID, item, transaction…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              {searchInput && (
                <button
                  type="button"
                  className="gpoh-search-clear"
                  onClick={() => setSearchInput("")}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>

            <div className="gpoh-status-chips">
              <button
                type="button"
                className={`gpoh-chip ${!filters.orderStatus ? "is-active" : ""}`}
                onClick={() => updateFilter("orderStatus", "")}
              >
                All
              </button>
              {ORDER_STATUSES.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`gpoh-chip gpoh-chip-${STATUS_TONE[s]} ${filters.orderStatus === s ? "is-active" : ""}`}
                  onClick={() => updateFilter("orderStatus", s)}
                >
                  {s}
                </button>
              ))}
            </div>

            <div className="gpoh-secondary-row">
              <div className="gpoh-field">
                <label>Payment</label>
                <select
                  value={filters.paymentStatus}
                  onChange={(e) => updateFilter("paymentStatus", e.target.value)}
                >
                  <option value="">All</option>
                  {PAYMENT_STATUSES.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div className="gpoh-field">
                <label>From</label>
                <input
                  type="date"
                  value={filters.from}
                  onChange={(e) => updateFilter("from", e.target.value)}
                />
              </div>
              <div className="gpoh-field">
                <label>To</label>
                <input
                  type="date"
                  value={filters.to}
                  onChange={(e) => updateFilter("to", e.target.value)}
                />
              </div>
              <div className="gpoh-field">
                <label>Sort by</label>
                <select
                  value={filters.sort}
                  onChange={(e) => updateFilter("sort", e.target.value)}
                >
                  {SORT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {isFiltering && (
              <div className="gpoh-active-chips">
                {activeChips.map((c) => (
                  <span className="gpoh-active-chip" key={c.key}>
                    {c.label}
                    <button type="button" onClick={() => removeChip(c.key)} aria-label="Remove">×</button>
                  </span>
                ))}
                <button type="button" className="gpoh-clear-all" onClick={clearAll}>
                  Clear all
                </button>
              </div>
            )}
          </div>

          {/* RESULTS COUNT */}
          {!loading && orders.length > 0 && (
            <div className="gpoh-results-count">
              Showing <strong>{orders.length}</strong> of <strong>{totalRecords}</strong> orders
            </div>
          )}

          {/* CONTENT */}
          {loading ? (
            <div className="gpoh-list">
              {[0, 1, 2, 3].map((i) => (
                <div className="gpoh-card gpoh-skeleton" key={i}>
                  <div className="gpoh-sk-line gpoh-sk-line-md" />
                  <div className="gpoh-sk-row">
                    <div className="gpoh-sk-thumb" />
                    <div className="gpoh-sk-meta">
                      <div className="gpoh-sk-line gpoh-sk-line-lg" />
                      <div className="gpoh-sk-line gpoh-sk-line-sm" />
                    </div>
                    <div className="gpoh-sk-line gpoh-sk-line-xs" />
                  </div>
                </div>
              ))}
            </div>
          ) : orders.length === 0 ? (
            <div className="gpoh-empty">
              <div className="gpoh-empty-icon">
                <svg viewBox="0 0 24 24" width="44" height="44" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 2l1 6h10l1-6" />
                  <path d="M5 6h14v15a1 1 0 01-1 1H6a1 1 0 01-1-1V6z" />
                  <line x1="9" y1="11" x2="15" y2="11" />
                </svg>
              </div>
              <h2>{isFiltering ? "No orders match your filters" : "No orders yet"}</h2>
              <p>
                {isFiltering
                  ? "Try adjusting your search or clearing filters to see more orders."
                  : "Start shopping and your gift cards will show up here."}
              </p>
              <div className="gpoh-empty-actions">
                {isFiltering && (
                  <button type="button" className="gpoh-btn gpoh-btn-ghost" onClick={clearAll}>
                    Clear filters
                  </button>
                )}
                <Link to="/shop" className="gpoh-btn gpoh-btn-primary">
                  Browse Gift Cards
                </Link>
              </div>

              {!isFiltering && (
                <div className="gpoh-empty-recos">
                  <h3>Hand-picked for you</h3>
                  <Recomended />
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="gpoh-list">
                {orders.map((order) => {
                  const items = order.orderItems || [];
                  const itemCount = items.length;
                  const firstItem = items[0];
                  const remaining = itemCount - 1;
                  const tone = STATUS_TONE[order.orderStatus] || "gold";
                  const payTone = order.isPaid
                    ? "green"
                    : PAYMENT_TONE[order.paymentStatus] || "gold";
                  const currencyPrefix =
                    order?.webhook?.currency === "USD" ? "$ " :
                    order?.webhook?.currency === "EUR" ? "€ " : "AED ";

                  return (
                    <div className="gpoh-card" key={order._id}>
                      <div className="gpoh-card-top">
                        <div className="gpoh-card-id">
                          <span className="gpoh-card-id-label">ORDER</span>
                          <span className="gpoh-card-id-val">{order._id}</span>
                        </div>
                        <div className="gpoh-card-pills">
                          <span className={`gpoh-status-pill gpoh-status-${tone}`}>
                            {order.orderStatus}
                          </span>
                          <span className={`gpoh-status-pill gpoh-status-${payTone}`}>
                            {order.isPaid ? "Paid" : (order.paymentStatus || "Pending")}
                          </span>
                        </div>
                      </div>

                      <div className="gpoh-card-mid">
                        <div className="gpoh-thumbs">
                          {items.slice(0, 3).map((it, i) => (
                            <div className="gpoh-thumb" key={i} style={{ zIndex: 3 - i }}>
                              {it?.proimg?.[0]?.src ? (
                                <img src={it.proimg[0].src} alt={it?.name} />
                              ) : (
                                <div className="gpoh-thumb-fallback" />
                              )}
                            </div>
                          ))}
                          {itemCount > 3 && (
                            <div className="gpoh-thumb gpoh-thumb-more">+{itemCount - 3}</div>
                          )}
                        </div>

                        <div className="gpoh-card-info">
                          <h4 className="gpoh-card-title">
                            {firstItem?.name || "Order"}
                            {remaining > 0 && (
                              <span className="gpoh-card-more"> &middot; +{remaining} more</span>
                            )}
                          </h4>
                          <div className="gpoh-card-meta">
                            <span>
                              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                              </svg>
                              {formatDate(order.createdAt)}
                            </span>
                            <span className="gpoh-card-dot">•</span>
                            <span>{order.method || "Bank Transfer"}</span>
                            <span className="gpoh-card-dot">•</span>
                            <span>{itemCount} {itemCount === 1 ? "item" : "items"}</span>
                          </div>
                        </div>

                        <div className="gpoh-card-amount">
                          <span className="gpoh-card-amt-label">Total</span>
                          <span className="gpoh-card-amt-val">
                            {currencyPrefix}{fmtMoney(order.totalPrice)}
                          </span>
                        </div>
                      </div>

                      <div className="gpoh-card-bottom">
                        <button type="button" className="gpoh-card-btn-secondary" onClick={() => openDrawer(order)}>
                          View Details
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="5" y1="12" x2="19" y2="12" />
                            <polyline points="12 5 19 12 12 19" />
                          </svg>
                        </button>
                        <Link to={`/success/${order._id}`} className="gpoh-card-btn-primary">
                          Track Order
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* PAGINATION */}
              {totalPages > 1 && (
                <div className="gpoh-pagination">
                  <button
                    type="button"
                    className="gpoh-page-btn"
                    onClick={() => updateFilter("page", Math.max(filters.page - 1, 1), false)}
                    disabled={filters.page <= 1}
                    aria-label="Previous"
                  >
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="15 18 9 12 15 6" />
                    </svg>
                  </button>
                  {Array.from({ length: totalPages }).slice(0, 7).map((_, i) => {
                    const pg = i + 1;
                    return (
                      <button
                        key={pg}
                        type="button"
                        className={`gpoh-page-btn ${filters.page === pg ? "is-active" : ""}`}
                        onClick={() => updateFilter("page", pg, false)}
                      >
                        {pg}
                      </button>
                    );
                  })}
                  {totalPages > 7 && <span className="gpoh-page-ellipsis">…</span>}
                  <button
                    type="button"
                    className="gpoh-page-btn"
                    onClick={() => updateFilter("page", Math.min(filters.page + 1, totalPages), false)}
                    disabled={filters.page >= totalPages}
                    aria-label="Next"
                  >
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* DRAWER */}
        <div className={`gpoh-drawer-backdrop ${drawerOpen ? "is-open" : ""}`} onClick={closeDrawer} />
        <aside className={`gpoh-drawer ${drawerOpen ? "is-open" : ""}`} aria-hidden={!drawerOpen}>
          {activeOrder && (
            <>
              <div className="gpoh-drawer-head">
                <div>
                  <span className="gpoh-card-id-label">ORDER</span>
                  <span className="gpoh-card-id-val">{activeOrder._id}</span>
                </div>
                <button type="button" className="gpoh-drawer-close" onClick={closeDrawer} aria-label="Close">×</button>
              </div>

              <div className="gpoh-drawer-body">
                <div className="gpoh-drawer-pills">
                  <span className={`gpoh-status-pill gpoh-status-${STATUS_TONE[activeOrder.orderStatus] || "gold"}`}>
                    {activeOrder.orderStatus}
                  </span>
                  <span className={`gpoh-status-pill gpoh-status-${activeOrder.isPaid ? "green" : (PAYMENT_TONE[activeOrder.paymentStatus] || "gold")}`}>
                    {activeOrder.isPaid ? "Paid" : (activeOrder.paymentStatus || "Pending")}
                  </span>
                </div>

                <div className="gpoh-drawer-section">
                  <h4>Items</h4>
                  <div className="gpoh-drawer-items">
                    {activeOrder.orderItems?.map((it, i) => (
                      <div className="gpoh-drawer-item" key={i}>
                        <div className="gpoh-thumb">
                          {it?.proimg?.[0]?.src ? <img src={it.proimg[0].src} alt={it?.name} /> : <div className="gpoh-thumb-fallback" />}
                        </div>
                        <div className="gpoh-drawer-item-meta">
                          <h5>{it.name}</h5>
                          {it.brand && it.brand !== it.name && <span className="gpoh-brand">{it.brand}</span>}
                          <span className="gpoh-drawer-item-qty">
                            {it.qty} × {(activeOrder?.webhook?.currency === "USD" ? "$" : activeOrder?.webhook?.currency === "EUR" ? "€" : "AED")} {it.price}
                          </span>
                        </div>
                        <div className="gpoh-drawer-item-total">
                          {(activeOrder?.webhook?.currency === "USD" ? "$" : activeOrder?.webhook?.currency === "EUR" ? "€" : "AED")} {fmtMoney(it.qty * it.price)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="gpoh-drawer-section">
                  <h4>Summary</h4>
                  <div className="gpoh-summary-rows">
                    <div className="gpoh-summary-row">
                      <span>Items</span>
                      <span>{activeOrder.orderItems?.length || 0}</span>
                    </div>
                    {Number(activeOrder.delivery_charge) > 0 && (
                      <div className="gpoh-summary-row">
                        <span>Delivery</span>
                        <span>AED {fmtMoney(activeOrder.delivery_charge)}</span>
                      </div>
                    )}
                    <div className="gpoh-summary-row gpoh-summary-total">
                      <span>Total</span>
                      <span className="gpoh-total-amt">
                        {(activeOrder?.webhook?.currency === "USD" ? "$" : activeOrder?.webhook?.currency === "EUR" ? "€" : "AED")} {fmtMoney(activeOrder.totalPrice)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="gpoh-drawer-section">
                  <h4>Payment</h4>
                  <div className="gpoh-meta-grid">
                    <div className="gpoh-meta-item"><span>Method</span><strong>{activeOrder.method || "—"}</strong></div>
                    <div className="gpoh-meta-item"><span>Status</span><strong>{activeOrder.paymentStatus || "Pending"}</strong></div>
                    <div className="gpoh-meta-item"><span>Transaction ID</span><strong className="gpoh-mono">{activeOrder.transactionId || "—"}</strong></div>
                    <div className="gpoh-meta-item"><span>Paid At</span><strong>{formatDate(activeOrder.paidAt)}</strong></div>
                    <div className="gpoh-meta-item"><span>Placed</span><strong>{formatDate(activeOrder.createdAt)}</strong></div>
                    <div className="gpoh-meta-item"><span>Updated</span><strong>{formatDate(activeOrder.updatedAt)}</strong></div>
                  </div>
                </div>

                {activeOrder.shippingAddress && (
                  <div className="gpoh-drawer-section">
                    <h4>Shipping Address</h4>
                    <div className="gpoh-address">
                      {activeOrder.shippingAddress.fullname && <p><strong>{activeOrder.shippingAddress.fullname}</strong></p>}
                      <p>{[activeOrder.shippingAddress.street, activeOrder.shippingAddress.addressLine2].filter(Boolean).join(", ")}</p>
                      <p>{[activeOrder.shippingAddress.city, activeOrder.shippingAddress.state, activeOrder.shippingAddress.postalCode].filter(Boolean).join(", ")}</p>
                      <p>{activeOrder.shippingAddress.country}</p>
                      {activeOrder.shippingAddress.phoneNumber && <p>📞 {activeOrder.shippingAddress.phoneNumber}</p>}
                    </div>
                  </div>
                )}

                <Link to={`/success/${activeOrder._id}`} className="gpoh-drawer-cta">
                  Open Full Order Page
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </Link>
              </div>
            </>
          )}
        </aside>

        <style>{`
          .gpoh-section {
            position: relative;
            background: linear-gradient(180deg, #07060a 0%, #0b0b0c 100%);
            color: #e6e8ec;
            padding: 60px 22px 90px;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
            overflow: hidden;
            min-height: 100vh;
          }
          .gpoh-bg-orb {
            position: absolute;
            width: 460px;
            height: 460px;
            border-radius: 50%;
            filter: blur(140px);
            opacity: 0.4;
            pointer-events: none;
          }
          .gpoh-bg-orb-tl { background: rgba(212,168,87,0.18); top: -180px; left: -120px; }
          .gpoh-bg-orb-br { background: rgba(212,168,87,0.12); bottom: -200px; right: -120px; }

          .gpoh-inner { position: relative; max-width: 1200px; margin: 0 auto; z-index: 1; }

          .gpoh-head { text-align: center; margin-bottom: 30px; }
          .gpoh-eyebrow {
            display: inline-flex; align-items: center; gap: 8px;
            padding: 6px 14px;
            background: rgba(212,168,87,0.1);
            border: 1px solid rgba(212,168,87,0.3);
            color: #d4a857;
            border-radius: 999px;
            font-size: 11px; font-weight: 700; letter-spacing: 2px;
            margin-bottom: 14px;
          }
          .gpoh-eyebrow-dot { width: 6px; height: 6px; border-radius: 50%; background: #d4a857; box-shadow: 0 0 10px #d4a857; animation: gpoh-pulse 1.6s ease-in-out infinite; }
          @keyframes gpoh-pulse { 0%,100% { opacity:1; transform: scale(1); } 50% { opacity:.55; transform: scale(.85); } }
          .gpoh-title {
            font-family: "Playfair Display", "Times New Roman", serif;
            font-size: 36px; font-weight: 700; color: #fff; margin: 0 0 10px; line-height: 1.2;
          }
          .gpoh-title-accent {
            background: linear-gradient(135deg, #f1cf85, #b58a3f);
            -webkit-background-clip: text; background-clip: text; color: transparent;
            font-style: italic; font-weight: 600;
          }
          .gpoh-sub { font-size: 14px; color: #9aa0a8; max-width: 540px; margin: 0 auto; line-height: 1.6; }

          /* STATS */
          .gpoh-stats {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 12px;
            margin: 22px 0;
          }
          .gpoh-stat {
            padding: 16px 18px;
            background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
            border: 1px solid rgba(212,168,87,0.18);
            border-radius: 14px;
            display: flex; flex-direction: column; gap: 6px;
          }
          .gpoh-stat-label { font-size: 11px; color: #9aa0a8; text-transform: uppercase; letter-spacing: 1.4px; font-weight: 700; }
          .gpoh-stat-val { font-size: 22px; color: #fff; font-weight: 700; font-family: "Playfair Display", serif; }
          .gpoh-stat-gold { background: linear-gradient(135deg, #f1cf85, #b58a3f); -webkit-background-clip: text; background-clip: text; color: transparent; }
          .gpoh-stat-green { color: #4ade80; }

          /* FILTER BAR */
          .gpoh-filter-bar {
            background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
            border: 1px solid rgba(212,168,87,0.18);
            border-radius: 16px;
            padding: 18px;
            margin-bottom: 16px;
          }
          .gpoh-search {
            position: relative;
            display: flex; align-items: center; gap: 8px;
            background: rgba(0,0,0,0.35);
            border: 1px solid rgba(212,168,87,0.22);
            border-radius: 12px;
            padding: 0 12px;
            margin-bottom: 14px;
            transition: border-color .2s ease;
          }
          .gpoh-search:focus-within { border-color: rgba(212,168,87,0.6); }
          .gpoh-search svg { color: #d4a857; flex-shrink: 0; }
          .gpoh-search input {
            flex: 1;
            background: transparent;
            border: none;
            outline: none;
            color: #fff;
            font-size: 14px;
            padding: 12px 0;
          }
          .gpoh-search input::placeholder { color: #6e7480; }
          .gpoh-search-clear {
            background: transparent; border: none; color: #9aa0a8;
            font-size: 22px; line-height: 1; cursor: pointer; padding: 0 4px;
          }
          .gpoh-search-clear:hover { color: #fff; }

          .gpoh-status-chips {
            display: flex; gap: 8px; flex-wrap: wrap;
            margin-bottom: 14px;
          }
          .gpoh-chip {
            padding: 7px 14px;
            background: rgba(255,255,255,0.03);
            border: 1px solid rgba(255,255,255,0.1);
            color: #c5c8cf;
            border-radius: 999px;
            font-size: 12px;
            font-weight: 700;
            cursor: pointer;
            transition: all .2s ease;
          }
          .gpoh-chip:hover {
            border-color: rgba(212,168,87,0.4);
            color: #fff;
          }
          .gpoh-chip.is-active {
            background: linear-gradient(135deg, #f1cf85, #b58a3f);
            border-color: #d4a857;
            color: #0b0b0c;
          }
          .gpoh-chip-green.is-active  { background: linear-gradient(135deg, #4ade80, #16a34a); border-color: #4ade80; color: #0b0b0c; }
          .gpoh-chip-red.is-active    { background: linear-gradient(135deg, #ff8a8a, #c0392b); border-color: #ff7a7a; color: #fff; }
          .gpoh-chip-blue.is-active   { background: linear-gradient(135deg, #60a5fa, #2563eb); border-color: #60a5fa; color: #fff; }

          .gpoh-secondary-row {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 12px;
          }
          .gpoh-field { display: flex; flex-direction: column; gap: 4px; }
          .gpoh-field label {
            font-size: 10.5px;
            color: #9aa0a8;
            text-transform: uppercase;
            letter-spacing: 1.2px;
            font-weight: 700;
          }
          .gpoh-field select,
          .gpoh-field input {
            background: rgba(0,0,0,0.35);
            border: 1px solid rgba(212,168,87,0.2);
            border-radius: 10px;
            color: #fff;
            font-size: 13px;
            padding: 10px 12px;
            outline: none;
            transition: border-color .2s ease;
            color-scheme: dark;
          }
          .gpoh-field select:focus,
          .gpoh-field input:focus { border-color: rgba(212,168,87,0.6); }
          .gpoh-field select option { background: #15110a; color: #fff; }

          .gpoh-active-chips {
            display: flex; flex-wrap: wrap; gap: 8px;
            padding-top: 14px;
            margin-top: 14px;
            border-top: 1px dashed rgba(212,168,87,0.18);
          }
          .gpoh-active-chip {
            display: inline-flex; align-items: center; gap: 6px;
            padding: 5px 6px 5px 12px;
            background: rgba(212,168,87,0.1);
            border: 1px solid rgba(212,168,87,0.28);
            color: #d4a857;
            border-radius: 999px;
            font-size: 11.5px;
            font-weight: 700;
          }
          .gpoh-active-chip button {
            background: rgba(212,168,87,0.2);
            border: none; color: #d4a857;
            width: 18px; height: 18px;
            border-radius: 50%;
            font-size: 14px; line-height: 1;
            cursor: pointer;
            display: inline-flex; align-items: center; justify-content: center;
          }
          .gpoh-active-chip button:hover { background: rgba(212,168,87,0.3); color: #f1cf85; }
          .gpoh-clear-all {
            background: transparent;
            border: none;
            color: #ff7a7a;
            font-size: 12px;
            font-weight: 700;
            cursor: pointer;
            padding: 5px 10px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .gpoh-clear-all:hover { color: #ff5050; }

          .gpoh-results-count {
            font-size: 13px;
            color: #9aa0a8;
            margin-bottom: 14px;
          }
          .gpoh-results-count strong { color: #d4a857; }

          /* CARD */
          .gpoh-list { display: flex; flex-direction: column; gap: 14px; }
          .gpoh-card {
            background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
            border: 1px solid rgba(212,168,87,0.2);
            border-radius: 16px;
            padding: 20px;
            transition: border-color .25s ease, transform .2s ease, box-shadow .25s ease;
          }
          .gpoh-card:hover {
            border-color: rgba(212,168,87,0.45);
            transform: translateY(-2px);
            box-shadow: 0 14px 30px rgba(212,168,87,0.1);
          }
          .gpoh-card-top {
            display: flex; justify-content: space-between; align-items: center;
            padding-bottom: 14px;
            border-bottom: 1px solid rgba(212,168,87,0.12);
            margin-bottom: 14px;
            flex-wrap: wrap; gap: 10px;
          }
          .gpoh-card-id { display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1; }
          .gpoh-card-id-label { font-size: 10.5px; color: #6e7480; text-transform: uppercase; letter-spacing: 1.4px; font-weight: 700; }
          .gpoh-card-id-val {
            font-size: 13px; color: #d4a857; font-weight: 700;
            font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
            word-break: break-all;
          }
          .gpoh-card-pills { display: flex; gap: 8px; flex-shrink: 0; }
          .gpoh-status-pill {
            padding: 4px 10px;
            border-radius: 999px;
            font-size: 10.5px;
            font-weight: 700;
            letter-spacing: 0.8px;
            text-transform: uppercase;
            white-space: nowrap;
          }
          .gpoh-status-gold  { background: rgba(212,168,87,0.12);  border: 1px solid rgba(212,168,87,0.32);  color: #d4a857; }
          .gpoh-status-green { background: rgba(74, 222, 128, 0.12); border: 1px solid rgba(74, 222, 128, 0.32); color: #4ade80; }
          .gpoh-status-red   { background: rgba(255, 90, 90, 0.12); border: 1px solid rgba(255, 90, 90, 0.32); color: #ff7a7a; }
          .gpoh-status-blue  { background: rgba(96, 165, 250, 0.12); border: 1px solid rgba(96, 165, 250, 0.32); color: #60a5fa; }

          .gpoh-card-mid {
            display: grid;
            grid-template-columns: auto 1fr auto;
            gap: 16px;
            align-items: center;
            margin-bottom: 14px;
          }
          .gpoh-thumbs { display: flex; }
          .gpoh-thumb {
            width: 48px; height: 48px;
            border-radius: 10px;
            overflow: hidden;
            background: #0b0b0c;
            border: 2px solid #15110a;
            margin-left: -10px;
            position: relative;
            flex-shrink: 0;
          }
          .gpoh-thumb:first-child { margin-left: 0; }
          .gpoh-thumb img { width: 100%; height: 100%; object-fit: cover; }
          .gpoh-thumb-fallback { width: 100%; height: 100%; background: linear-gradient(135deg, #1a1208, #0b0b0c); }
          .gpoh-thumb-more {
            display: flex; align-items: center; justify-content: center;
            background: rgba(212,168,87,0.14);
            color: #d4a857;
            font-weight: 800;
            font-size: 11px;
          }

          .gpoh-card-info { min-width: 0; }
          .gpoh-card-title {
            font-size: 15px; color: #fff; font-weight: 600;
            margin: 0 0 6px;
            line-height: 1.35;
            display: -webkit-box;
            -webkit-line-clamp: 1;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
          .gpoh-card-more { color: #d4a857; font-weight: 700; }
          .gpoh-card-meta {
            display: flex; gap: 8px; align-items: center; flex-wrap: wrap;
            font-size: 12px; color: #9aa0a8;
          }
          .gpoh-card-meta span { display: inline-flex; align-items: center; gap: 4px; }
          .gpoh-card-meta svg { color: #d4a857; }
          .gpoh-card-dot { color: #6e7480 !important; }

          .gpoh-card-amount { display: flex; flex-direction: column; align-items: flex-end; gap: 2px; flex-shrink: 0; }
          .gpoh-card-amt-label { font-size: 10.5px; color: #9aa0a8; text-transform: uppercase; letter-spacing: 1.4px; font-weight: 700; }
          .gpoh-card-amt-val {
            font-size: 18px; font-weight: 800;
            background: linear-gradient(135deg, #f1cf85, #b58a3f);
            -webkit-background-clip: text; background-clip: text; color: transparent;
            font-family: "Playfair Display", serif;
          }

          .gpoh-card-bottom {
            display: flex; gap: 10px; justify-content: flex-end;
            padding-top: 12px;
            border-top: 1px dashed rgba(212,168,87,0.12);
          }
          .gpoh-card-btn-secondary,
          .gpoh-card-btn-primary {
            display: inline-flex; align-items: center; gap: 8px;
            padding: 9px 18px;
            border-radius: 10px;
            font-size: 12px;
            font-weight: 700;
            letter-spacing: 0.5px;
            text-transform: uppercase;
            cursor: pointer;
            text-decoration: none;
            border: none;
            transition: transform .2s ease, box-shadow .25s ease, background .2s ease, gap .2s ease;
          }
          .gpoh-card-btn-secondary {
            background: rgba(255,255,255,0.03);
            border: 1px solid rgba(212,168,87,0.3);
            color: #d4a857;
          }
          .gpoh-card-btn-secondary:hover {
            background: rgba(212,168,87,0.1);
            border-color: rgba(212,168,87,0.55);
            color: #f1cf85;
            gap: 12px;
          }
          .gpoh-card-btn-primary {
            background: linear-gradient(135deg, #f1cf85, #d4a857 50%, #b58a3f);
            color: #0b0b0c;
          }
          .gpoh-card-btn-primary:hover {
            transform: translateY(-1px);
            box-shadow: 0 10px 22px rgba(212,168,87,0.3);
            color: #0b0b0c;
            text-decoration: none;
          }

          /* PAGINATION */
          .gpoh-pagination {
            display: flex; gap: 6px; justify-content: center;
            margin-top: 28px;
            flex-wrap: wrap;
          }
          .gpoh-page-btn {
            min-width: 38px; height: 38px;
            background: rgba(255,255,255,0.03);
            border: 1px solid rgba(212,168,87,0.22);
            border-radius: 10px;
            color: #c5c8cf;
            font-size: 13px;
            font-weight: 700;
            cursor: pointer;
            display: flex; align-items: center; justify-content: center;
            padding: 0 10px;
            transition: all .2s ease;
          }
          .gpoh-page-btn:hover:not(:disabled) {
            border-color: rgba(212,168,87,0.55);
            color: #d4a857;
          }
          .gpoh-page-btn.is-active {
            background: linear-gradient(135deg, #f1cf85, #b58a3f);
            border-color: #d4a857;
            color: #0b0b0c;
          }
          .gpoh-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
          .gpoh-page-ellipsis { color: #6e7480; align-self: center; padding: 0 4px; }

          /* EMPTY */
          .gpoh-empty {
            background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
            border: 1px solid rgba(212,168,87,0.22);
            border-radius: 20px;
            padding: 50px 30px;
            text-align: center;
          }
          .gpoh-empty-icon {
            width: 90px; height: 90px;
            margin: 0 auto 20px;
            background: rgba(212,168,87,0.1);
            border: 1px solid rgba(212,168,87,0.28);
            color: #d4a857;
            border-radius: 50%;
            display: flex; align-items: center; justify-content: center;
          }
          .gpoh-empty h2 {
            font-family: "Playfair Display", "Times New Roman", serif;
            font-size: 26px; color: #fff; font-weight: 700; margin: 0 0 8px;
          }
          .gpoh-empty p { font-size: 14px; color: #9aa0a8; margin: 0 0 22px; }
          .gpoh-empty-actions { display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; }
          .gpoh-empty-recos { margin-top: 40px; padding-top: 30px; border-top: 1px dashed rgba(212,168,87,0.18); }
          .gpoh-empty-recos h3 {
            font-family: "Playfair Display", "Times New Roman", serif;
            font-size: 22px; color: #fff; margin: 0 0 14px;
          }

          .gpoh-btn {
            display: inline-flex; align-items: center; gap: 10px;
            padding: 12px 24px;
            border-radius: 12px;
            font-size: 12.5px;
            font-weight: 800;
            letter-spacing: 0.5px;
            text-transform: uppercase;
            cursor: pointer;
            text-decoration: none;
            border: none;
            transition: transform .2s ease, box-shadow .25s ease, background .2s ease;
          }
          .gpoh-btn-primary {
            background: linear-gradient(135deg, #f1cf85, #d4a857 50%, #b58a3f);
            color: #0b0b0c;
          }
          .gpoh-btn-primary:hover {
            transform: translateY(-2px);
            box-shadow: 0 14px 30px rgba(212,168,87,0.35);
            color: #0b0b0c; text-decoration: none;
          }
          .gpoh-btn-ghost {
            background: rgba(255,255,255,0.03);
            border: 1px solid rgba(212,168,87,0.3);
            color: #d4a857;
          }
          .gpoh-btn-ghost:hover {
            background: rgba(212,168,87,0.1);
            border-color: rgba(212,168,87,0.55);
            color: #f1cf85;
          }

          /* SKELETON */
          .gpoh-skeleton .gpoh-sk-line {
            height: 12px;
            border-radius: 6px;
            background: rgba(255,255,255,0.05);
            animation: gpoh-shimmer 1.4s ease infinite;
          }
          .gpoh-skeleton .gpoh-sk-line-md { width: 35%; margin-bottom: 14px; }
          .gpoh-skeleton .gpoh-sk-line-lg { width: 70%; height: 14px; }
          .gpoh-skeleton .gpoh-sk-line-sm { width: 50%; height: 10px; margin-top: 8px; }
          .gpoh-skeleton .gpoh-sk-line-xs { width: 60px; height: 14px; }
          .gpoh-skeleton .gpoh-sk-row {
            display: grid; grid-template-columns: 48px 1fr auto; gap: 14px; align-items: center;
          }
          .gpoh-skeleton .gpoh-sk-thumb {
            width: 48px; height: 48px; border-radius: 10px;
            background: rgba(255,255,255,0.05);
            animation: gpoh-shimmer 1.4s ease infinite;
          }
          @keyframes gpoh-shimmer { 0%,100% { opacity: .5; } 50% { opacity: 1; } }

          /* DRAWER */
          .gpoh-drawer-backdrop {
            position: fixed; inset: 0;
            background: rgba(0,0,0,0.6);
            backdrop-filter: blur(4px);
            opacity: 0;
            pointer-events: none;
            transition: opacity .3s ease;
            z-index: 998;
          }
          .gpoh-drawer-backdrop.is-open { opacity: 1; pointer-events: auto; }
          .gpoh-drawer {
            position: fixed; top: 0; right: 0; bottom: 0;
            width: min(520px, 100vw);
            background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
            border-left: 1px solid rgba(212,168,87,0.25);
            box-shadow: -20px 0 50px rgba(0,0,0,0.5);
            transform: translateX(100%);
            transition: transform .35s cubic-bezier(0.32, 0.72, 0, 1);
            z-index: 999;
            display: flex; flex-direction: column;
            color: #e6e8ec;
          }
          .gpoh-drawer.is-open { transform: translateX(0); }
          .gpoh-drawer-head {
            display: flex; justify-content: space-between; align-items: center;
            padding: 20px 24px;
            border-bottom: 1px solid rgba(212,168,87,0.18);
            flex-shrink: 0;
          }
          .gpoh-drawer-close {
            background: rgba(255,255,255,0.05);
            border: 1px solid rgba(255,255,255,0.1);
            color: #fff;
            width: 32px; height: 32px;
            border-radius: 8px;
            font-size: 22px; line-height: 1;
            cursor: pointer;
            display: flex; align-items: center; justify-content: center;
          }
          .gpoh-drawer-close:hover { background: rgba(212,168,87,0.15); border-color: rgba(212,168,87,0.4); color: #d4a857; }
          .gpoh-drawer-body { padding: 22px 24px 28px; overflow-y: auto; flex: 1; }
          .gpoh-drawer-pills { display: flex; gap: 8px; margin-bottom: 22px; flex-wrap: wrap; }

          .gpoh-drawer-section { margin-bottom: 24px; }
          .gpoh-drawer-section h4 {
            font-size: 11.5px;
            color: #d4a857;
            text-transform: uppercase;
            letter-spacing: 1.6px;
            font-weight: 800;
            margin: 0 0 12px;
          }
          .gpoh-drawer-items { display: flex; flex-direction: column; gap: 10px; }
          .gpoh-drawer-item {
            display: grid;
            grid-template-columns: 48px 1fr auto;
            gap: 12px;
            align-items: center;
            padding: 12px;
            background: rgba(255,255,255,0.02);
            border: 1px solid rgba(255,255,255,0.06);
            border-radius: 12px;
          }
          .gpoh-drawer-item .gpoh-thumb { margin: 0; border: 1px solid rgba(212,168,87,0.2); }
          .gpoh-drawer-item-meta h5 { font-size: 14px; color: #fff; margin: 0 0 4px; font-weight: 600; }
          .gpoh-brand { display: block; font-size: 11px; color: #d4a857; font-weight: 600; letter-spacing: 0.4px; margin-bottom: 2px; }
          .gpoh-drawer-item-qty { font-size: 12px; color: #9aa0a8; }
          .gpoh-drawer-item-total { font-size: 14px; color: #d4a857; font-weight: 700; white-space: nowrap; }

          .gpoh-summary-rows { display: flex; flex-direction: column; gap: 10px; }
          .gpoh-summary-row {
            display: flex; justify-content: space-between; align-items: center;
            font-size: 14px; color: #c5c8cf;
          }
          .gpoh-summary-row span:last-child { color: #fff; font-weight: 600; }
          .gpoh-summary-total {
            margin-top: 8px; padding-top: 12px;
            border-top: 1px solid rgba(212,168,87,0.22);
            font-size: 16px;
          }
          .gpoh-summary-total span:first-child { font-family: "Playfair Display", serif; color: #fff; font-weight: 700; font-size: 17px; }
          .gpoh-total-amt {
            font-size: 20px !important;
            background: linear-gradient(135deg, #f1cf85, #b58a3f);
            -webkit-background-clip: text; background-clip: text; color: transparent !important;
            font-weight: 800 !important;
          }

          .gpoh-meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
          .gpoh-meta-item {
            display: flex; flex-direction: column; gap: 4px;
            padding: 10px 12px;
            background: rgba(255,255,255,0.02);
            border: 1px solid rgba(255,255,255,0.06);
            border-radius: 10px;
          }
          .gpoh-meta-item span {
            font-size: 10px; color: #6e7480; text-transform: uppercase; letter-spacing: 1.2px; font-weight: 700;
          }
          .gpoh-meta-item strong { font-size: 12.5px; color: #fff; font-weight: 600; word-break: break-all; }
          .gpoh-mono { font-family: ui-monospace, Menlo, Consolas, monospace; color: #d4a857 !important; font-size: 11.5px !important; }

          .gpoh-address {
            padding: 14px;
            background: rgba(255,255,255,0.02);
            border: 1px solid rgba(255,255,255,0.06);
            border-radius: 12px;
          }
          .gpoh-address p { font-size: 13px; color: #c5c8cf; margin: 0 0 4px; line-height: 1.5; }
          .gpoh-address p strong { color: #fff; }

          .gpoh-drawer-cta {
            display: flex; align-items: center; justify-content: center; gap: 10px;
            width: 100%;
            padding: 14px;
            background: linear-gradient(135deg, #f1cf85, #d4a857 50%, #b58a3f);
            color: #0b0b0c;
            border-radius: 12px;
            font-size: 13px; font-weight: 800;
            letter-spacing: 0.5px;
            text-transform: uppercase;
            text-decoration: none;
            transition: transform .2s ease, box-shadow .25s ease;
          }
          .gpoh-drawer-cta:hover { transform: translateY(-2px); box-shadow: 0 14px 30px rgba(212,168,87,0.35); color: #0b0b0c; text-decoration: none; }

          @media (max-width: 991px) {
            .gpoh-stats { grid-template-columns: repeat(2, 1fr); }
            .gpoh-secondary-row { grid-template-columns: repeat(2, 1fr); }
          }
          @media (max-width: 720px) {
            .gpoh-card-mid { grid-template-columns: auto 1fr; }
            .gpoh-card-amount { grid-column: 1 / -1; flex-direction: row; align-items: center; justify-content: space-between; padding-top: 10px; border-top: 1px dashed rgba(212,168,87,0.15); }
            .gpoh-card-amt-val { font-size: 22px; }
            .gpoh-meta-grid { grid-template-columns: 1fr; }
            .gpoh-card-bottom { flex-direction: column; }
            .gpoh-card-btn-secondary, .gpoh-card-btn-primary { width: 100%; justify-content: center; }
          }
          @media (max-width: 575px) {
            .gpoh-section { padding: 40px 14px 70px; }
            .gpoh-title { font-size: 26px; }
            .gpoh-stats { grid-template-columns: 1fr 1fr; }
            .gpoh-secondary-row { grid-template-columns: 1fr; }
            .gpoh-stat-val { font-size: 18px; }
          }
        `}</style>
      </section>

      <Footer />
    </Fragment>
  );
};

const mapStateToProps = (state) => ({
  carts: state.cartList.cart,
  configs: state.configs,
  token: state.configs?.token,
});

export default connect(mapStateToProps)(YourOrdersPage);
